import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Clock, MapPin, Users, Ticket, Target, Compass } from 'lucide-react'
import { useCurrencyStore, useLanguageStore } from '../../lib/store'
import { formatPrice } from '../../lib/utils'
import { toursApi } from '../../lib/api'

interface Tour {
    id: number
    type: string
    price: number
    duration: string
    location: string
    maxParticipants: number
    translations?: {
        en?: { title?: string; description?: string }
        vi?: { title?: string; description?: string }
    }
}

export default function ToursPage() {
    useTranslation()
    const { currency } = useCurrencyStore()
    const { language } = useLanguageStore()

    const [tours, setTours] = useState<Tour[]>([])
    const [loading, setLoading] = useState(true)
    const [activeCategory, setActiveCategory] = useState('all')

    const categories = [
        { id: 'all', name: { en: 'All', vi: 'Tất cả' }, Icon: Target },
        { id: 'tour', name: { en: 'Tours', vi: 'Tour' }, Icon: Compass },
        { id: 'ticket', name: { en: 'Tickets', vi: 'Vé' }, Icon: Ticket },
    ]

    useEffect(() => {
        const fetchTours = async () => {
            setLoading(true)
            try {
                const params: any = {}
                if (activeCategory !== 'all') params.type = activeCategory

                const response = await toursApi.list(params)
                if (response.data?.tours) {
                    setTours(response.data.tours)
                }
            } catch (error) {
                console.error('Failed to fetch tours:', error)
            } finally {
                setLoading(false)
            }
        }
        fetchTours()
    }, [activeCategory])

    const getTitle = (tour: Tour) => {
        if (language === 'en') return tour.translations?.en?.title || 'Untitled'
        return tour.translations?.vi?.title || tour.translations?.en?.title || 'Chưa có tiêu đề'
    }
    const getDescription = (tour: Tour) => {
        if (language === 'en') return tour.translations?.en?.description || ''
        return tour.translations?.vi?.description || tour.translations?.en?.description || ''
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-8">
            <div className="container mx-auto px-4">
                {/* Hero Header */}
                <div className="text-center mb-10">
                    <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-primary-600 to-blue-600 bg-clip-text text-transparent">
                        {language === 'en' ? 'Tours & Tickets' : 'Tour & Vé'}
                    </h1>
                    <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                        {language === 'en'
                            ? 'Discover amazing experiences in Phú Quốc - from island hopping to theme parks'
                            : 'Khám phá trải nghiệm tuyệt vời tại Phú Quốc - từ du ngoạn đảo đến công viên giải trí'}
                    </p>
                </div>

                {/* Categories */}
                <div className="flex justify-center gap-3 mb-10">
                    {categories.map((cat) => (
                        <button
                            key={cat.id}
                            onClick={() => setActiveCategory(cat.id)}
                            className={`px-6 py-3 rounded-full font-medium transition-all flex items-center gap-2 ${activeCategory === cat.id
                                ? 'bg-primary-600 text-white shadow-lg shadow-primary-200'
                                : 'bg-white border hover:border-primary-500 hover:text-primary-600'
                                }`}
                        >
                            <cat.Icon className="w-4 h-4" />
                            {cat.name[language]}
                        </button>
                    ))}
                </div>

                {/* Tours Grid */}
                {loading ? (
                    <div className="text-center py-12 text-gray-500">
                        {language === 'en' ? 'Loading tours...' : 'Đang tải...'}
                    </div>
                ) : tours.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                        {language === 'en' ? 'No tours found' : 'Không tìm thấy tour'}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {tours.map((tour) => (
                            <Link
                                key={tour.id}
                                to={`/tours/${tour.id}`}
                                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border"
                            >
                                <div className="aspect-[4/3] bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center relative">
                                    {tour.type === 'tour' ? (
                                        <MapPin className="w-16 h-16 text-blue-300" />
                                    ) : (
                                        <Ticket className="w-16 h-16 text-purple-300" />
                                    )}
                                    <span className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${tour.type === 'tour'
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-purple-500 text-white'
                                        }`}>
                                        {tour.type === 'tour' ? <><Compass className="w-3 h-3" /> Tour</> : <><Ticket className="w-3 h-3" /> Ticket</>}
                                    </span>
                                </div>
                                <div className="p-5">
                                    <h3 className="font-bold text-lg mb-2 group-hover:text-primary-600 transition line-clamp-2">
                                        {getTitle(tour) || 'Untitled'}
                                    </h3>
                                    <p className="text-gray-500 text-sm mb-3 line-clamp-2">
                                        {getDescription(tour) || (language === 'en' ? 'Experience the best of Phú Quốc' : 'Trải nghiệm tuyệt vời tại Phú Quốc')}
                                    </p>
                                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                                        <span className="flex items-center gap-1">
                                            <Clock className="w-4 h-4" />
                                            {tour.duration || 'N/A'}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Users className="w-4 h-4" />
                                            {tour.maxParticipants || 0}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between pt-4 border-t gap-2">
                                        <div className="min-w-0">
                                            <span className="text-xs text-gray-400">
                                                {language === 'en' ? 'From' : 'Từ'}
                                            </span>
                                            <p className="text-lg font-bold text-primary-600 whitespace-nowrap">
                                                {formatPrice(tour.price, currency)}
                                            </p>
                                        </div>
                                        <span className="px-3 py-2 bg-primary-600 text-white rounded-lg text-xs font-medium group-hover:bg-primary-700 transition whitespace-nowrap">
                                            {language === 'en' ? 'Book Now' : 'Đặt ngay'}
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
